import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Boxes, AlertTriangle, Sliders, ArrowLeftRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { inv, ADJUST_REASONS, REASON_LABELS } from '../../lib/invApi';
import ScanButton from '../../components/inventory/ScanButton';
import RestockDialog from '../../components/inventory/RestockDialog';
import { Truck } from 'lucide-react';

const InventoryStock = () => {
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [lowOnly, setLowOnly] = useState(params.get('low') === '1');
  const [q, setQ] = useState('');
  const [adjOpen, setAdjOpen] = useState(false);
  const [restockOpen, setRestockOpen] = useState(false);
  const [adj, setAdj] = useState({ item_id: '', item_name: '', location_id: '', location_name: '', current: 0, target: 0, mode: 'delta', delta: '', newQty: '', targetQty: '', reason_code: 'INITIAL', reason_notes: '' });

  useEffect(() => {
    Promise.all([
      inv.get('/stock', { low_only: lowOnly }).then((r) => r.data),
      inv.get('/locations').then((r) => r.data),
    ]).then(([s, l]) => { setRows(s); setLocations(l); });
  }, [lowOnly]);

  const filtered = q ? rows.filter((r) => `${r.item_name} ${r.item_sku} ${r.barcode} ${r.category}`.toLowerCase().includes(q.toLowerCase())) : rows;

  const reloadStock = async () => { const fresh = await inv.get('/stock', { low_only: lowOnly }); setRows(fresh.data); };

  const openAdj = (r, locId, locName) => {
    const s = (r.stock || []).find((x) => x.location_id === locId);
    const cur = s ? s.quantity : 0;
    const tgt = s ? (s.target_qty || 0) : 0;
    setAdj({ item_id: r.item_id, item_name: r.item_name, location_id: locId, location_name: locName, current: cur, target: tgt, mode: 'delta', delta: '', newQty: String(cur), targetQty: tgt ? String(tgt) : '', reason_code: 'INITIAL', reason_notes: '' });
    setAdjOpen(true);
  };

  // Delta from either "adjust amount" or "set new quantity" mode.
  const effectiveDelta = () => {
    if (adj.mode === 'set') {
      if (adj.newQty === '' || isNaN(parseFloat(adj.newQty))) return null;
      return parseFloat(adj.newQty) - parseFloat(adj.current);
    }
    if (adj.delta === '' || isNaN(parseFloat(adj.delta))) return null;
    return parseFloat(adj.delta);
  };

  const submitAdj = async () => {
    const d = effectiveDelta();
    const newTarget = adj.targetQty === '' ? 0 : parseFloat(adj.targetQty);
    const targetChanged = !isNaN(newTarget) && newTarget !== (adj.target || 0);
    // Target-only save: no quantity entered but the Target/Par changed → save target,
    // no movement. Only block when there's neither a quantity change nor a target change.
    if (d === null && !targetChanged) { toast.error('Enter a quantity or set a target'); return; }
    try {
      if (targetChanged) {
        await inv.put('/stock/target', { item_id: adj.item_id, location_id: adj.location_id, target_qty: newTarget });
      }
      if (d !== null && d !== 0) {
        await inv.post('/adjust', { item_id: adj.item_id, location_id: adj.location_id, delta: d, reason_code: adj.reason_code, reason_notes: adj.reason_notes });
      }
      toast.success((d !== null && d !== 0) ? 'Stock adjusted' : 'Target saved');
      setAdjOpen(false);
      await reloadStock();
    } catch (err) { toast.error(err.response?.data?.detail || 'Adjust failed'); }
  };

  return (
    <div data-testid="inv-stock-page" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-3"><Boxes className="w-7 h-7 text-[#D4AF37]" /> Stock by Location</h2>
          <p className="text-[#A3A3A3]">Shop &amp; Warehouse = exact on hand. Van = <span className="text-amber-300/90">Estimated On Van</span> — a rough reference from transfers in; it drifts because material is not scanned out on jobs. Click any cell to adjust with an audit-coded reason.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
            <input data-testid="inv-stock-low-toggle" type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} className="accent-[#D4AF37]" />
            Low stock only
          </label>
          <Button data-testid="inv-stock-restock-btn" onClick={() => setRestockOpen(true)} variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"><Truck className="w-4 h-4 mr-2" /> Restock From Targets</Button>
          <ScanButton mode="scan" testId="stock-scan-btn" onDone={reloadStock} />
        </div>
      </div>

      <Input data-testid="inv-stock-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, SKU, barcode, category…" className="bg-[#0A0A0A] border-[#262626] text-white" />

      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#262626]">
            <tr>
              <th className="text-left px-3 py-2 text-[#A3A3A3] sticky left-0 bg-[#0A0A0A]">Item</th>
              {locations.map((l) => (
                <th key={l.id} className="text-right px-3 py-2 text-[#A3A3A3] min-w-[100px]">
                  {l.name}
                  <span className="block text-[9px] uppercase text-[#525252]">{l.type}{l.type === 'van' && <span data-testid={`inv-stock-est-${l.id}`} className="text-amber-300/80"> · estimated</span>}</span>
                </th>
              ))}
              <th className="text-right px-3 py-2 text-[#A3A3A3] bg-[#0A0A0A]/50" title="Exact company inventory: Shop + Warehouse only (vans never included)">Shop + WH Total</th>
              <th className="text-right px-3 py-2 text-[#A3A3A3]">Value</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={locations.length + 3} className="px-4 py-6 text-center text-[#525252]">No stock to display.</td></tr>
            ) : filtered.map((r) => {
              const perLoc = Object.fromEntries(r.stock.map((s) => [s.location_id, s]));
              return (
                <tr key={r.item_id} data-testid={`inv-stock-row-${r.item_id}`} className="border-b border-[#262626] last:border-0">
                  <td className="px-3 py-2 sticky left-0 bg-[#141414]">
                    <p className="text-white">{r.item_name}{r.low && <AlertTriangle className="w-3.5 h-3.5 text-amber-400 inline ml-1.5" />}</p>
                    <p className="text-[#525252] text-xs">{r.item_sku || '—'} · {r.unit}</p>
                  </td>
                  {locations.map((l) => {
                    const s = perLoc[l.id];
                    const qty = s ? s.quantity : 0;
                    const low = s && s.low;
                    const target = s ? (s.target_qty || 0) : 0;
                    const shortage = s ? (s.shortage || 0) : 0;
                    return (
                      <td key={l.id} className="px-3 py-2 text-right">
                        <button
                          data-testid={`inv-stock-cell-${r.item_id}-${l.id}`}
                          onClick={() => openAdj(r, l.id, l.name)}
                          className="text-right hover:opacity-80"
                          title="Click to adjust quantity or set target/par"
                        >
                          <span className={`text-sm ${low ? 'text-amber-400 font-bold' : qty > 0 ? 'text-white' : 'text-[#525252]'} ${l.type === 'van' ? 'italic' : ''}`} title={l.type === 'van' ? 'Estimated / last known van stock — not a physical count' : undefined}>{l.type === 'van' && qty ? '~' : ''}{qty}</span>
                          {target > 0 && (
                            <span className="block text-[10px] leading-tight" data-testid={`inv-stock-target-${r.item_id}-${l.id}`}>
                              <span className="text-[#525252]">tgt {target}</span>
                              {shortage > 0 && <span className="text-red-400 font-semibold"> · need {shortage}</span>}
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right text-white font-semibold">{r.company_total}</td>
                  <td className="px-3 py-2 text-right text-[#D4AF37]">${r.company_value.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent data-testid="inv-adjust-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sliders className="w-4 h-4 text-[#D4AF37]" /> Adjust Stock</DialogTitle>
            <DialogDescription className="text-[#A3A3A3] text-xs">
              {adj.item_name} @ {adj.location_name} — every change is recorded with reason + user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-[#0A0A0A] border border-[#262626] rounded-md px-3 py-2">
              <span className="text-xs text-[#A3A3A3]">Current quantity</span>
              <span data-testid="inv-adjust-current" className="text-lg font-bold text-white">{adj.current}</span>
            </div>

            <div className="flex gap-1 bg-[#0A0A0A] border border-[#262626] rounded-md p-1">
              <button data-testid="inv-adjust-mode-delta" onClick={() => setAdj({ ...adj, mode: 'delta' })} className={`flex-1 text-xs py-1.5 rounded ${adj.mode === 'delta' ? 'bg-[#D4AF37] text-[#0A0A0A] font-semibold' : 'text-[#A3A3A3]'}`}>Adjust by amount</button>
              <button data-testid="inv-adjust-mode-set" onClick={() => setAdj({ ...adj, mode: 'set' })} className={`flex-1 text-xs py-1.5 rounded ${adj.mode === 'set' ? 'bg-[#D4AF37] text-[#0A0A0A] font-semibold' : 'text-[#A3A3A3]'}`}>Set new quantity</button>
            </div>

            {adj.mode === 'delta' ? (
              <div>
                <Label className="text-xs">Adjustment amount (negative to remove)</Label>
                <Input data-testid="inv-adjust-delta" type="number" step="any" value={adj.delta} onChange={(e) => setAdj({ ...adj, delta: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="e.g. +5 or -2" />
                {adj.delta !== '' && !isNaN(parseFloat(adj.delta)) && <p className="text-[11px] text-[#A3A3A3] mt-1">New quantity will be <span className="text-white">{parseFloat(adj.current) + parseFloat(adj.delta)}</span></p>}
              </div>
            ) : (
              <div>
                <Label className="text-xs">New quantity</Label>
                <Input data-testid="inv-adjust-newqty" type="number" step="any" value={adj.newQty} onChange={(e) => setAdj({ ...adj, newQty: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                {adj.newQty !== '' && !isNaN(parseFloat(adj.newQty)) && <p className="text-[11px] text-[#A3A3A3] mt-1">Ledger change: <span className="text-white">{(parseFloat(adj.newQty) - parseFloat(adj.current)) >= 0 ? '+' : ''}{parseFloat(adj.newQty) - parseFloat(adj.current)}</span></p>}
              </div>
            )}

            <div>
              <Label className="text-xs">Reason *</Label>
              <Select value={adj.reason_code} onValueChange={(v) => setAdj({ ...adj, reason_code: v })}>
                <SelectTrigger data-testid="inv-adjust-reason" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {ADJUST_REASONS.map((r) => <SelectItem key={r} value={r}>{REASON_LABELS[r] || r.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input data-testid="inv-adjust-notes" value={adj.reason_notes} onChange={(e) => setAdj({ ...adj, reason_notes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>

            <div className="border-t border-[#262626] pt-3">
              <Label className="text-xs">Target / Par at {adj.location_name} <span className="text-[#525252]">(separate from Company Minimum — does not change stock)</span></Label>
              <Input data-testid="inv-adjust-target" type="number" step="any" min="0" value={adj.targetQty} onChange={(e) => setAdj({ ...adj, targetQty: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="e.g. 4" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={() => setAdjOpen(false)} variant="outline" className="border-[#262626]">Cancel</Button>
            <Button data-testid="inv-adjust-submit" onClick={submitAdj} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Apply</Button>
          </div>
        </DialogContent>
      </Dialog>

      <RestockDialog open={restockOpen} onOpenChange={setRestockOpen} locations={locations} onDone={reloadStock} />
    </div>
  );
};

export default InventoryStock;
